import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { PILLAR_NAMES, NUM_ACTIVE_PILLARS } from '../data/pillars';
import { 
  BarChart3, Save, Send, ChevronLeft, CheckCircle, 
  TrendingUp, Database, Upload, AlertCircle
} from 'lucide-react';

const QuantitativeAssessmentPage = () => {
  const { assessmentId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [currentPillar, setCurrentPillar] = useState(1);
  const [responses, setResponses] = useState({});
  const [saving, setSaving] = useState(false);
  const [phase, setPhase] = useState(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const organizationId = location.state?.organizationId || localStorage.getItem('organization_id') || 'b8b52a08-165e-4a21-85ae-099e7268a29e';

  // Quantitative questions template (simplified version - 10 per pillar)
  const quantitativeQuestions = {
    1: [
      { id: 'Q1_DATA1', question: 'Number of ethics training hours per employee annually', type: 'number', unit: 'hours' },
      { id: 'Q1_DATA2', question: 'Percentage of leadership team with ethics certification', type: 'percentage' },
      { id: 'Q1_DATA3', question: 'Number of ethics violations reported in last 12 months', type: 'number' },
      { id: 'Q1_DATA4', question: 'Percentage of suppliers/vendors assessed for ethical practices', type: 'percentage' },
      { id: 'Q1_DATA5', question: 'Average response time to ethics concerns (in days)', type: 'number', unit: 'days' }
    ],
    2: [
      { id: 'Q2_DATA1', question: 'Percentage of diverse representation in workforce', type: 'percentage' },
      { id: 'Q2_DATA2', question: 'Pay equity ratio (lowest to highest paid employee)', type: 'ratio' },
      { id: 'Q2_DATA3', question: 'Number of DEI training sessions conducted annually', type: 'number' },
      { id: 'Q2_DATA4', question: 'Employee satisfaction score from DEI survey', type: 'score', min: 1, max: 10 },
      { id: 'Q2_DATA5', question: 'Percentage of leadership from underrepresented groups', type: 'percentage' }
    ],
    3: [
      { id: 'Q3_DATA1', question: 'Number of employee grievances resolved annually', type: 'number' },
      { id: 'Q3_DATA2', question: 'Employee freedom of expression rating (1-10)', type: 'score', min: 1, max: 10 },
      { id: 'Q3_DATA3', question: 'Percentage of employees feeling psychologically safe', type: 'percentage' },
      { id: 'Q3_DATA4', question: 'Number of anonymous feedback channels available', type: 'number' },
      { id: 'Q3_DATA5', question: 'Average response time to employee concerns (hours)', type: 'number', unit: 'hours' }
    ],
    4: [
      { id: 'Q4_DATA1', question: 'Carbon emissions reduction (percentage vs baseline)', type: 'percentage' },
      { id: 'Q4_DATA2', question: 'Percentage of renewable energy used', type: 'percentage' },
      { id: 'Q4_DATA3', question: 'Waste recycling rate', type: 'percentage' },
      { id: 'Q4_DATA4', question: 'Number of sustainability initiatives implemented', type: 'number' },
      { id: 'Q4_DATA5', question: 'Environmental budget as percentage of revenue', type: 'percentage' }
    ],
    5: [
      { id: 'Q5_DATA1', question: 'Average training hours per employee annually', type: 'number', unit: 'hours' },
      { id: 'Q5_DATA2', question: 'Percentage of employees with learning development plans', type: 'percentage' },
      { id: 'Q5_DATA3', question: 'Number of internal certifications/courses offered', type: 'number' },
      { id: 'Q5_DATA4', question: 'Learning budget per employee', type: 'currency' },
      { id: 'Q5_DATA5', question: 'Employee skill development satisfaction score (1-10)', type: 'score', min: 1, max: 10 }
    ],
    6: [
      { id: 'Q6_DATA1', question: 'Employee wellbeing program participation rate', type: 'percentage' },
      { id: 'Q6_DATA2', question: 'Number of mental health support sessions provided', type: 'number' },
      { id: 'Q6_DATA3', question: 'Employee assistance program utilization rate', type: 'percentage' },
      { id: 'Q6_DATA4', question: 'Compassion culture rating from employee survey (1-10)', type: 'score', min: 1, max: 10 },
      { id: 'Q6_DATA5', question: 'Volunteer hours contributed by employees annually', type: 'number', unit: 'hours' }
    ],
    7: [
      { id: 'Q7_DATA1', question: 'Business continuity plan testing frequency (times/year)', type: 'number' },
      { id: 'Q7_DATA2', question: 'Average time to adapt to major changes (weeks)', type: 'number', unit: 'weeks' },
      { id: 'Q7_DATA3', question: 'Percentage of employees cross-trained', type: 'percentage' },
      { id: 'Q7_DATA4', question: 'Innovation initiatives implemented annually', type: 'number' },
      { id: 'Q7_DATA5', question: 'Organizational resilience score (1-10)', type: 'score', min: 1, max: 10 }
    ],
    8: [
      { id: 'Q8_DATA1', question: 'Employee engagement score (1-10)', type: 'score', min: 1, max: 10 },
      { id: 'Q8_DATA2', question: 'Percentage of employees aligned with company mission', type: 'percentage' },
      { id: 'Q8_DATA3', question: 'Employee retention rate', type: 'percentage' },
      { id: 'Q8_DATA4', question: 'Purpose-driven initiative investments (percentage of revenue)', type: 'percentage' },
      { id: 'Q8_DATA5', question: 'Employee satisfaction with meaningful work (1-10)', type: 'score', min: 1, max: 10 }
    ]
  };

  const pillarNames = PILLAR_NAMES;

  useEffect(() => {
    fetchPhase();
    fetchExistingResponses();
  }, []);

  const fetchPhase = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/assessment-phases/${assessmentId}/${organizationId}`);
      const data = await response.json();
      setPhase(data);
      
      // Check if payment completed
      if (data.payment_status !== 'completed') {
        navigate('/org-admin/assessments');
      }
    } catch (error) {
      console.error('Error fetching phase:', error);
    }
  };

  const fetchExistingResponses = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/org-responses/${assessmentId}/${organizationId}?phase=quantitative`);
      const data = await response.json();
      
      const responsesMap = {};
      data.forEach(r => {
        responsesMap[r.question_id] = r.response_data || {};
      });
      setResponses(responsesMap);
    } catch (error) {
      console.error('Error fetching responses:', error);
    }
  };

  const handleInputChange = (questionId, value) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: { ...prev[questionId], value: value }
    }));
  };

  const handleSavePillar = async (status = 'draft') => {
    setSaving(true);
    try {
      const pillarQuestions = quantitativeQuestions[currentPillar];
      
      for (const question of pillarQuestions) {
        const responseData = {
          assessment_id: assessmentId,
          organization_id: organizationId,
          question_id: question.id,
          phase: 'quantitative',
          pillar_number: currentPillar,
          response_data: responses[question.id] || {},
          status: status,
          submitted_by_user_id: organizationId
        };

        await fetch(`${BACKEND_URL}/api/org-responses`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(responseData)
        });
      }

      fetchPhase();
    } catch (error) {
      console.error('Error saving responses:', error);
    } finally {
      setSaving(false);
    }
  };

  const getCompletionStats = () => {
    let completed = 0;
    for (let pillar = 1; pillar <= 8; pillar++) {
      const pillarQuestions = quantitativeQuestions[pillar];
      const allFilled = pillarQuestions.every(q => responses[q.id]?.value);
      if (allFilled) completed++;
    }
    return { completed, total: 8, percentage: Math.round((completed / 8) * 100) };
  };

  const stats = getCompletionStats();
  const currentQuestions = quantitativeQuestions[currentPillar];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-purple-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white sticky top-0 z-10 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/org-admin/assessments')}
                className="p-2 hover:bg-white/20 rounded-lg transition-all"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <div>
                <h1 className="text-2xl font-black">Quantitative Assessment</h1>
                <p className="text-purple-100 text-sm">Data-driven metrics across all 6 pillars</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-purple-100">Progress</p>
              <p className="text-2xl font-bold">{stats.completed}/{NUM_ACTIVE_PILLARS} Pillars</p>
              <div className="w-32 bg-white/20 rounded-full h-2 mt-1">
                <div
                  className="bg-white h-2 rounded-full transition-all"
                  style={{ width: `${stats.percentage}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-12 gap-6">
          {/* Sidebar */}
          <div className="col-span-3">
            <div className="bg-white rounded-xl shadow-lg p-6 sticky top-24">
              <h3 className="font-bold text-gray-900 mb-4">Pillars</h3>
              <div className="space-y-2">
                {[1, 2, 3, 4, 5, 6, 7, 8].map(pillar => {
                  const pillarQs = quantitativeQuestions[pillar];
                  const completed = pillarQs.filter(q => responses[q.id]?.value).length;
                  
                  return (
                    <button
                      key={pillar}
                      onClick={() => setCurrentPillar(pillar)}
                      className={`w-full text-left px-4 py-3 rounded-lg transition-all ${
                        currentPillar === pillar
                          ? 'bg-purple-600 text-white shadow-lg'
                          : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-sm">Pillar {pillar}</span>
                        <span className="text-xs">{completed}/{pillarQs.length}</span>
                      </div>
                      {completed === pillarQs.length && (
                        <CheckCircle className="w-4 h-4 text-green-500 absolute right-2" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Main Form Area */}
          <div className="col-span-9">
            <div className="bg-white rounded-xl shadow-xl overflow-hidden">
              {/* Pillar Header */}
              <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white p-6">
                <h2 className="text-2xl font-black mb-1">Pillar {currentPillar}</h2>
                <p className="text-purple-100">{pillarNames[currentPillar]}</p>
              </div>

              {/* Questions Form */}
              <div className="p-8">
                <div className="space-y-6">
                  {currentQuestions.map((question, index) => (
                    <div key={question.id} className="border border-gray-200 rounded-lg p-6 hover:border-purple-300 transition-all">
                      <div className="flex items-start gap-4">
                        <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-sm font-bold text-purple-600">{index + 1}</span>
                        </div>
                        <div className="flex-1">
                          <label className="block text-sm font-bold text-gray-900 mb-3">
                            {question.question}
                          </label>
                          <div className="flex items-center gap-3">
                            <input
                              type={question.type === 'currency' ? 'number' : question.type === 'percentage' || question.type === 'ratio' || question.type === 'score' ? 'number' : 'number'}
                              value={responses[question.id]?.value || ''}
                              onChange={(e) => handleInputChange(question.id, e.target.value)}
                              min={question.min}
                              max={question.max}
                              step={question.type === 'currency' ? '0.01' : question.type === 'percentage' ? '0.1' : '1'}
                              className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-lg font-semibold"
                              placeholder={
                                question.type === 'percentage' ? '0-100%' :
                                question.type === 'score' ? `${question.min}-${question.max}` :
                                question.type === 'currency' ? '$0.00' :
                                'Enter value'
                              }
                            />
                            {question.unit && (
                              <span className="text-gray-600 font-semibold">{question.unit}</span>
                            )}
                            {question.type === 'percentage' && (
                              <span className="text-gray-600 font-semibold">%</span>
                            )}
                            {question.type === 'currency' && (
                              <span className="text-gray-600 font-semibold">USD</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
                  <button
                    onClick={() => setCurrentPillar(Math.max(1, currentPillar - 1))}
                    disabled={currentPillar === 1}
                    className="flex items-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 transition-all disabled:opacity-50"
                  >
                    <ChevronLeft className="w-5 h-5" />
                    Previous Pillar
                  </button>

                  <div className="flex gap-3">
                    <button
                      onClick={() => handleSavePillar('draft')}
                      disabled={saving}
                      className="flex items-center gap-2 px-6 py-3 bg-purple-100 text-purple-700 rounded-lg font-semibold hover:bg-purple-200 transition-all"
                    >
                      <Save className="w-5 h-5" />
                      {saving ? 'Saving...' : 'Save Draft'}
                    </button>

                    <button
                      onClick={() => {
                        handleSavePillar('submitted');
                        if (currentPillar < 8) {
                          setTimeout(() => setCurrentPillar(currentPillar + 1), 500);
                        }
                      }}
                      disabled={saving}
                      className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-all"
                    >
                      <Send className="w-5 h-5" />
                      {currentPillar === 8 ? 'Submit Final' : 'Save & Next'}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Info Card */}
            {stats.completed === 8 && (
              <div className="mt-6 bg-gradient-to-r from-green-50 to-teal-50 border-2 border-green-500 rounded-xl p-6">
                <div className="flex items-center gap-4">
                  <CheckCircle className="w-12 h-12 text-green-600 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-bold text-green-900 mb-1">Quantitative Assessment Complete!</h3>
                    <p className="text-green-700 text-sm">Your assessment is now ready for evaluator review. You'll be notified once the evaluation is complete.</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuantitativeAssessmentPage;
